import React, { useState, useEffect } from 'react';
import SubsectionTitle from '../SubsectionTitle';
import Calendar from './Calendar';
import HourIntervals from './HourIntervals';
import { getFormattedDateInfo } from '../../utils/functions';

export default function DesiredDateSelector({ gadgetApi, settings, cartAttributes, setCartAttributes, hourRef }) {
	if (!settings || !cartAttributes) return null;

	const parseDateFromCart = (dateString) => {
		const [day, month, year] = dateString.split('-').map(Number);
		const date = new Date(year, month - 1, day);

		date.setHours(0, 0, 0, 0);

		return date;
	};

	const formatDateForCart = (date) => {
		const day = date.getDate().toString().padStart(2, '0');
		const month = (date.getMonth() + 1).toString().padStart(2, '0');
		const year = date.getFullYear();

		return `${day}-${month}-${year}`;
	};

	// Check if a date is selectable (same logic as Calendar component)
	const isDateSelectable = (date) => {
		if (!date) return false;

		const today = new Date();

		today.setHours(0, 0, 0, 0);

		// Parse disabled dates from settings (format: "1.11.2025, 12.11.2025, 13.11.2025")
		const parseDisabledDates = (disabledDatesString) => {
			if (!disabledDatesString) return [];

			return disabledDatesString?.split(',').map((dateStr) => {
				const [day, month, year] = dateStr.trim()?.split('.');

				return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
			});
		};

		const disabledDates = parseDisabledDates(settings?.disabledDays);

		// Calculate min and max dates
		const minDate = new Date();
		const maxDate = new Date();

		maxDate.setDate(maxDate.getDate() + parseInt(settings?.maxDays || 30));

		// Check if date is before minDate
		if (minDate && date < minDate) return false;

		// Check if date is after maxDate
		if (maxDate && date > maxDate) return false;

		// Check if date is in disabled dates
		const isDisabled = disabledDates.some((disabledDate) => {
			return date.toDateString() === disabledDate.toDateString();
		});

		if (isDisabled) return false;

		return date >= today;
	};

	// Find the next available date starting from today
	const getNextAvailableDate = () => {
		const today = new Date();

		today.setHours(0, 0, 0, 0);

		const maxDate = new Date();

		maxDate.setDate(maxDate.getDate() + parseInt(settings?.maxDays || 30));

		let checkDate = new Date(today);

		while (checkDate <= maxDate) {
			if (isDateSelectable(checkDate)) {
				return checkDate;
			}

			checkDate.setDate(checkDate.getDate() + 1);
		}

		// Fallback to today if no date found (shouldn't happen)
		return today;
	};

	const getInitialDate = () => {
		if (cartAttributes.desiredDate) {
			const existingDate = parseDateFromCart(cartAttributes.desiredDate);

			if (isDateSelectable(existingDate)) {
				return existingDate;
			}
		}

		return getNextAvailableDate();
	};

	const [selectedDate, setSelectedDate] = useState(getInitialDate);

	// Initialize and validate date on mount or when cartAttributes changes
	useEffect(() => {
		// If no date in cart, set it to next available date
		if (!cartAttributes.desiredDate) {
			const nextAvailable = getNextAvailableDate();
			const formattedDate = formatDateForCart(nextAvailable);

			setCartAttributes((prev) => {
				if (prev.desiredDate === formattedDate) return prev;

				return {
					...prev,
					desiredDate: formattedDate,
				};
			});

			setSelectedDate(nextAvailable);

			return;
		}

		const existingDate = parseDateFromCart(cartAttributes.desiredDate);

		// Check if date is disabled or invalid
		if (!isDateSelectable(existingDate)) {
			const nextAvailable = getNextAvailableDate();
			const formattedDate = formatDateForCart(nextAvailable);

			setCartAttributes((prev) => {
				if (prev.desiredDate === formattedDate) return prev;

				return {
					...prev,
					desiredDate: formattedDate,
				};
			});
			setSelectedDate(nextAvailable);
		} else {
			// If date is valid (future and selectable), ensure selectedDate matches
			if (selectedDate.getTime() !== existingDate.getTime()) {
				setSelectedDate(existingDate);
			}
		}
	}, [cartAttributes.desiredDate, settings?.disabledDays, settings?.maxDays]);

	// Update cart when selectedDate changes
	useEffect(() => {
		if (!selectedDate) return;

		const formattedDate = formatDateForCart(selectedDate);

		setCartAttributes((prev) => {
			if (prev.desiredDate === formattedDate) return prev;
			return {
				...prev,
				desiredDate: formattedDate,
			};
		});
	}, [selectedDate, setCartAttributes]);

	const handleDateChange = (date) => {
		setSelectedDate(date);
	};

	const showDeliveryNotice =
		settings.intervalDeliveryNotice && cartAttributes.deliveryMethod === 'delivery' && cartAttributes.desiredTime;

	return (
		<div className="desired-date-selector">
			<SubsectionTitle settings={settings} stepNumber={2} cartAttributes={cartAttributes} />

			<div className="desired-date-selector__content can-be-closed">
				{settings.desiredDateNotice && (
					<div className="desired-date-notice">
						<p className="text-base">{settings.desiredDateNotice}</p>
					</div>
				)}

				<div className="desired-date-selector__inputs-container">
					<Calendar value={selectedDate} onChange={handleDateChange} settings={settings} />

					<div className="desired-date-selector__hour-intervals-confirmation">
						<HourIntervals
							settings={settings}
							cartAttributes={cartAttributes}
							setCartAttributes={setCartAttributes}
							gadgetApi={gadgetApi}
							hourRef={hourRef}
						/>

						{cartAttributes.intervalUnavailable && (
							<div className="hour-intervals__error">
								<p className="text-error">{settings.intervalNoLongerAvailable}</p>
							</div>
						)}

						{showDeliveryNotice && (
							<p className="text-sm selected-info">{settings.intervalDeliveryNotice}</p>
						)}

						{selectedDate && (
							<p className="text-lg selected-info">
								{getFormattedDateInfo(selectedDate, cartAttributes.desiredTime, settings)}
							</p>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
